import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  IndianRupee, TrendingUp, Award, Calendar,
  Star, RefreshCw, Info, ChevronUp
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverEarnings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate, period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('observer_token');
      
      const res = await axios.get(`${BACKEND_URL}/api/observer/earnings?token=${token}&period=${period}`);
      setData(res.data);
    } catch (error) {
      console.error('Error loading earnings:', error);
      if (error.response?.status === 401) {
        navigate('/observer/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'grade_a': return 'from-amber-500 to-yellow-500';
      case 'grade_b': return 'from-slate-400 to-slate-500';
      case 'grade_c': return 'from-orange-600 to-orange-700';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getGradeLabel = (grade) => {
    switch (grade) {
      case 'grade_a': return 'A';
      case 'grade_b': return 'B';
      case 'grade_c': return 'C';
      default: return '-';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  const earnings = data?.earnings || {};
  const rateCard = data?.rate_card || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="observer-earnings">
      <ObserverNav />
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <IndianRupee className="w-7 h-7 text-emerald-600" />
                My Earnings
              </h1>
              <p className="text-slate-500">Track your income and grade progression</p>
            </div>
            <div className="flex gap-2">
              <select 
                value={period} 
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Time</option>
                <option value="month">This Month</option>
                <option value="week">This Week</option>
              </select>
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Current Grade & Total Earnings */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Current Grade */}
            <Card className={`bg-gradient-to-br ${getGradeColor(data?.current_grade)} text-white border-0`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm mb-1">Current Grade</p>
                    <p className="text-5xl font-bold">{getGradeLabel(data?.current_grade)}</p>
                    <p className="text-white/80 text-sm mt-2">
                      {data?.current_grade_label}
                    </p>
                  </div>
                  <Award className="w-16 h-16 text-white/30" />
                </div>
              </CardContent>
            </Card>

            {/* Total Earnings */}
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">Total Earnings</p>
                    <p className="text-3xl font-bold">{formatCurrency(earnings.total_earnings)}</p>
                    <p className="text-emerald-100 text-sm mt-2">
                      {earnings.total_sessions || 0} sessions
                    </p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-emerald-200" />
                </div>
              </CardContent>
            </Card>

            {/* Current Rate */}
            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-100 text-sm mb-1">Current Rate</p>
                    <p className="text-3xl font-bold">{formatCurrency(data?.current_rate)}</p>
                    <p className="text-violet-100 text-sm mt-2">
                      Per session
                    </p>
                  </div>
                  <IndianRupee className="w-12 h-12 text-violet-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sessions by Grade */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Sessions by Grade</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold text-amber-600">A</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">
                    {earnings.sessions_by_grade?.grade_a || 0}
                  </p>
                  <p className="text-sm text-slate-500">Grade A Sessions</p>
                </div>
                
                <div className="p-4 bg-slate-100 rounded-xl text-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold text-slate-600">B</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">
                    {earnings.sessions_by_grade?.grade_b || 0}
                  </p>
                  <p className="text-sm text-slate-500">Grade B Sessions</p>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-xl text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold text-orange-600">C</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">
                    {earnings.sessions_by_grade?.grade_c || 0}
                  </p>
                  <p className="text-sm text-slate-500">Grade C Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Grade Progression */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ChevronUp className="w-5 h-5 text-emerald-600" />
                Grade Progression
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    {/* Grade C */}
                    <div className={`flex-1 p-3 rounded-lg ${data?.months_active <= 1 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-gray-100'}`}>
                      <p className="font-semibold text-center">Grade C</p>
                      <p className="text-sm text-center text-slate-500">1st Month</p>
                    </div>
                    <div className="text-gray-300">→</div>
                    {/* Grade B */}
                    <div className={`flex-1 p-3 rounded-lg ${data?.months_active === 2 ? 'bg-slate-200 border-2 border-slate-400' : 'bg-gray-100'}`}>
                      <p className="font-semibold text-center">Grade B</p>
                      <p className="text-sm text-center text-slate-500">2nd Month</p>
                    </div>
                    <div className="text-gray-300">→</div>
                    {/* Grade A */}
                    <div className={`flex-1 p-3 rounded-lg ${data?.months_active >= 3 ? 'bg-amber-100 border-2 border-amber-400' : 'bg-gray-100'}`}>
                      <p className="font-semibold text-center">Grade A</p>
                      <p className="text-sm text-center text-slate-500">3rd Month+</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 text-center">
                    You have been active for <strong>{data?.months_active || 1} month(s)</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-blue-600" />
                Observer Rate Card
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Grade</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Tenure</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Full Rate</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Trial Rate (50%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-orange-50">
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                          Grade C
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">1st Month</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                        {formatCurrency(rateCard.grade_c?.full)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500">
                        {formatCurrency(rateCard.grade_c?.trial)}
                      </td>
                    </tr>
                    <tr className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full font-medium">
                          Grade B
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">2nd Month</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                        {formatCurrency(rateCard.grade_b?.full)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500">
                        {formatCurrency(rateCard.grade_b?.trial)}
                      </td>
                    </tr>
                    <tr className="hover:bg-amber-50">
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                          Grade A
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">3rd Month+</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                        {formatCurrency(rateCard.grade_a?.full)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500">
                        {formatCurrency(rateCard.grade_a?.trial)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 p-3 bg-violet-50 rounded-lg text-sm text-violet-700">
                <strong>Trial Period:</strong> First {data?.trial_sessions || 7} sessions per child are at 50% rate.
                Your grade increases automatically based on tenure.
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default ObserverEarnings;
